import { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import NextUpBanner from "@/components/NextUpBanner";
import {
  CalendarDays, ClipboardList, Users, BarChart3,
  PhoneOutgoing, Search, Sunrise, Sun, TrendingUp, AlertTriangle, Cake,
  Trophy, TrendingDown, Minus, Rocket, GraduationCap, Phone,
} from "lucide-react";
import {
  DashCard, SessionPill, Pill, Skel, TutorAuditTile,
  todayISO, parseHM, nowHM, ord,
} from "@/pages/dashboard/DashboardParts";
import {
  RankingDialog, ImprovementDialog, SingleCallDialog, BatchCallDialog,
} from "@/pages/dashboard/DashboardDialogs";

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canCallParents = user?.role === "admin" || user?.role === "teacher";
  const [cn, setCn] = useState(localStorage.getItem("sa_selected_class") || "");
  const [sec, setSec] = useState(localStorage.getItem("sa_selected_section") || "");
  const [classMeta, setClassMeta] = useState({ classes: [], assignments: [] });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  // Top-3 most-engaged students from the teacher's currently-selected class
  // + section this week. Powered by the same /audit/tuition endpoint the
  // standalone Activity Audit page uses (server-side teacher scoping).
  const [tutorTop, setTutorTop] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [mix, setMix] = useState(null);
  const [callTarget, setCallTarget] = useState(null);   // {student fields..., mode: 'congrats'|'concern'}
  const [batchTarget, setBatchTarget] = useState(null); // {mode, students:[...]}
  const [callSending, setCallSending] = useState(false);
  const [rankOpen, setRankOpen] = useState(false);
  const [improveOpen, setImproveOpen] = useState(false);
  const [marksDetail, setMarksDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadMarksDetail = async () => {
    if (!cn || !sec) return;
    setDetailLoading(true);
    try {
      const { data } = await api.get("/teacher/marks-detail", { params: { class_name: cn, section: sec } });
      setMarksDetail(data);
    } catch {
      setMarksDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };
  const openRanking = () => { setRankOpen(true); loadMarksDetail(); };
  const openImprovement = () => { setImproveOpen(true); loadMarksDetail(); };

  const improvers = (marksDetail?.improvement || []).filter((s) => s.jump > 0);
  const droppers = (marksDetail?.improvement || []).filter((s) => s.jump < 0);

  useEffect(() => {
    if (!cn || !sec) return;
    setLeaderboard(null);
    setMix(null);
    api.get("/teacher/leaderboard", { params: { class_name: cn, section: sec } })
      .then(({ data }) => setLeaderboard(data)).catch(() => {});
    api.get("/teacher/performance-mix", { params: { class_name: cn, section: sec } })
      .then(({ data }) => setMix(data)).catch(() => {});
  }, [cn, sec]);

  const singleMessage = (s, mode) => {
    const exam = (marksDetail?.exam_label || leaderboard?.exam_label) || "the latest exam";
    if (mode === "concern") {
      return `We wish to bring to your attention that your ward ${s.name} of Class ${cn} Section ${sec} has shown a decline in ${exam}, scoring ${s.percentage}% — down ${Math.abs(s.jump)}% from the previous exam. We kindly request your support and encouragement at home to help them get back on track.`;
    }
    return `Congratulations! Your ward ${s.name} of Class ${cn} Section ${sec} has shown remarkable improvement in ${exam}, rising to ${s.percentage}% — a jump of ${s.jump}% over the previous exam. We are very proud of their effort. Please encourage them to keep up the same hard work and maintain this wonderful progress.`;
  };

  const batchMessage = (mode) => {
    const exam = (marksDetail?.exam_label || leaderboard?.exam_label) || "the latest exam";
    if (mode === "concern") {
      return `We wish to bring to your attention that your ward's performance has declined in ${exam} for Class ${cn} Section ${sec}. We kindly request your support and encouragement at home to help them improve in the coming exams.`;
    }
    return `We are delighted to share that your ward has shown excellent improvement in ${exam} for Class ${cn} Section ${sec}. We are proud of their effort and request you to keep encouraging them so they maintain this wonderful progress.`;
  };

  const sendSingleCall = async () => {
    if (!callTarget) return;
    setCallSending(true);
    try {
      await api.post("/announcements/calls", {
        student_ids: [callTarget.student_id],
        message: singleMessage(callTarget, callTarget.mode),
        category: callTarget.mode,
      });
      toast.success(`Call queued to ${callTarget.name}'s parent`);
      setCallTarget(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not place the call. Check Retell setup.");
    } finally {
      setCallSending(false);
    }
  };

  const sendBatchCalls = async () => {
    if (!batchTarget?.students?.length) return;
    setCallSending(true);
    try {
      const ids = batchTarget.students.map((s) => s.student_id);
      await api.post("/announcements/calls", { student_ids: ids, message: batchMessage(batchTarget.mode), category: batchTarget.mode });
      toast.success(`${ids.length} call${ids.length > 1 ? "s" : ""} queued (1 per second)`);
      setBatchTarget(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not place the calls. Check Retell setup.");
    } finally {
      setCallSending(false);
    }
  };

  // Load assignable classes once.
  useEffect(() => {
    api.get("/meta/classes").then(({ data }) => setClassMeta(data)).catch(() => {});
  }, []);

  // Choose the default class once BOTH the full user (with class_teacher_of) and
  // the class list are available — always prefer the section the teacher is
  // class teacher of, so the dashboard opens on their own class on every load /
  // refresh. Falls back to the first assignment only for non-class-teachers.
  useEffect(() => {
    if (!user) return;
    const pairs = classMeta.assignments || [];
    if (!pairs.length) return;
    const ct = (user.class_teacher_of || [])[0];
    if (ct && pairs.some((a) => a.class_name === ct.class_name && a.section === ct.section)) {
      if (cn !== ct.class_name || sec !== ct.section) selectClass(ct.class_name, ct.section);
      return;
    }
    const hasCurrent = cn && sec && pairs.some((a) => a.class_name === cn && a.section === sec);
    if (!hasCurrent) selectClass(pairs[0].class_name, pairs[0].section);
    // eslint-disable-next-line
  }, [user, classMeta]);

  const selectClass = (c, s) => {
    localStorage.setItem("sa_selected_class", c);
    localStorage.setItem("sa_selected_section", s);
    setCn(c); setSec(s);
  };

  const classOptions = classMeta.classes?.length ? classMeta.classes : (cn ? [cn] : []);
  const sectionOptions = (() => {
    const secs = (classMeta.assignments || []).filter((a) => a.class_name === cn).map((a) => a.section);
    const uniq = [...new Set(secs)].sort();
    return uniq.length ? uniq : (sec ? [sec] : []);
  })();

  useEffect(() => {
    if (!cn || !sec) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/audit/tuition", {
          params: { range: "week", class_name: cn, section: sec },
        });
        if (!cancelled) setTutorTop(data);
      } catch { if (!cancelled) setTutorTop({ students: [], totals: {} }); }
    })();
    return () => { cancelled = true; };
  }, [cn, sec]);

  useEffect(() => {
    if (!cn || !sec) return undefined;
    let cancelled = false;
    const load = async (isInitial) => {
      if (isInitial) setLoading(true);
      try {
        const { data } = await api.get("/teacher/stats/overview", {
          params: { class_name: cn, section: sec },
        });
        if (!cancelled) setStats(data);
      } catch {
        if (!cancelled) setStats(null);
      } finally {
        if (!cancelled && isInitial) setLoading(false);
      }
    };
    load(true);
    const id = setInterval(() => load(false), 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [cn, sec]);

  // Admins/super-admins get the Admin dashboard, not the teacher class view
  // (covers direct nav to /dashboard and returning from a simulation).
  if (user && !user.is_dev_admin && (user.role === "admin" || user.role === "super_admin")) {
    return <Navigate to="/admin/dashboard" replace />;
  }
  // UtilityAdmin has its own Configuration portal — never the teacher/admin view.
  if (user && !user.is_dev_admin && user.role === "utility_admin") {
    return <Navigate to="/utility/settings" replace />;
  }

  if (!cn || !sec) {
    return (
      <Layout>
        <div className="py-20 text-center text-slate-500" data-testid="no-class-assigned">
          No classes assigned to you yet. Please contact your administrator.
        </div>
      </Layout>
    );
  }

  const classLabel = ["LKG", "UKG", "Play Group"].includes(cn) ? cn : `${cn}${ord(cn)} Standard`;
  const today = todayISO();

  const am = stats?.attendance_today?.morning;
  const pm = stats?.attendance_today?.afternoon;
  const periods = stats?.timetable_today?.periods || [];
  const now = nowHM();
  const nextPeriod = periods.find((p) => now < parseHM(p.start_time || p.time));

  const att30 = stats?.analytics_30d?.percentage ?? 0;
  const atRisk = stats?.analytics_30d?.at_risk_count ?? 0;
  const attTone = att30 >= 90 ? "emerald" : att30 >= 75 ? "sky" : "rose";

  return (
    <Layout>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500 mb-2">Today&apos;s class</div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-slate-100" style={{ fontFamily: "Outfit" }}>
            Class <span className="text-sky-600 dark:text-sky-400">{classLabel}</span> · Section <span className="text-sky-600 dark:text-sky-400">{sec}</span>
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">{stats?.enrolled ?? "—"} students enrolled · {today}</p>
        </div>
        <div className="flex flex-col items-end gap-2.5">
          <div className="flex flex-wrap justify-end gap-2.5">
            <NextUpBanner holidayEndpoint="/holidays" holidayTo="/holidays" eventEndpoint="/events" eventTo="/events" />
          </div>
          <div className="flex items-center gap-2" data-testid="dashboard-class-switcher">
            <Select value={cn} onValueChange={(v) => {
              const firstSec = (classMeta.assignments || []).filter((a) => a.class_name === v).map((a) => a.section).sort()[0];
              selectClass(v, firstSec || sec);
            }}>
              <SelectTrigger className="h-11 w-32 rounded-full" data-testid="dash-class-select"><SelectValue /></SelectTrigger>
              <SelectContent>{classOptions.map((c) => <SelectItem key={c} value={c}>{["LKG","UKG","Play Group"].includes(c) ? c : `Class ${c}`}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={sec} onValueChange={(v) => selectClass(cn, v)}>
              <SelectTrigger className="h-11 w-28 rounded-full" data-testid="dash-section-select"><SelectValue /></SelectTrigger>
              <SelectContent>{sectionOptions.map((s) => <SelectItem key={s} value={s}>Sec {s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="teacher-overview-cards">
        {/* Take Attendance */}
        <DashCard
          testid="overview-card-attendance"
          tone="emerald"
          icon={ClipboardList}
          title="Take attendance"
          subtitle="AM / PM today"
          onClick={() => navigate("/attendance")}
        >
          {loading ? <Skel /> : (
            <div className="space-y-2">
              <div className="flex items-center gap-3 text-sm">
                <SessionPill label="AM" icon={Sunrise} session={am} />
                <SessionPill label="PM" icon={Sun} session={pm} />
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400">
                {am?.submitted || pm?.submitted
                  ? `${(am?.present || 0) + (pm?.present || 0)} present · ${(am?.absent || 0) + (pm?.absent || 0)} absent today`
                  : "Not marked yet — tap to start."}
              </div>
            </div>
          )}
        </DashCard>

        {/* Timetable */}
        <DashCard
          testid="overview-card-timetable"
          tone="sky"
          icon={CalendarDays}
          title="Timetable"
          subtitle="Today"
          onClick={() => navigate("/timetable")}
        >
          {loading ? <Skel /> : periods.length === 0 ? (
            <div className="text-sm text-slate-500">No periods scheduled.</div>
          ) : (
            <div className="space-y-2">
              <div className="text-3xl font-bold text-sky-600 dark:text-sky-300" style={{ fontFamily: "Outfit" }}>
                {periods.length} <span className="text-sm text-slate-500 font-normal">periods</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                <span className="inline-flex items-center gap-1"><Sunrise className="h-3 w-3 text-sky-500" />{stats?.timetable_today?.am_count ?? 0} AM</span>
                <span className="text-slate-300 dark:text-slate-600">·</span>
                <span className="inline-flex items-center gap-1"><Sun className="h-3 w-3 text-amber-500" />{stats?.timetable_today?.pm_count ?? 0} PM</span>
              </div>
              {nextPeriod && (
                <div className="text-[11px] text-sky-600 dark:text-sky-300 font-medium truncate">
                  Next: {nextPeriod.start_time} · {nextPeriod.subject}
                </div>
              )}
            </div>
          )}
        </DashCard>

        {/* Top performers — latest exam leaderboard */}
        <DashCard
          testid="overview-card-leaderboard"
          tone="amber"
          icon={Trophy}
          title="Top performers"
          subtitle={leaderboard?.exam_label || "Latest exam"}
          onClick={openRanking}
        >
          {loading || leaderboard === null ? <Skel /> : ((leaderboard.top || leaderboard.top3)?.length ?? 0) === 0 ? (
            <div className="text-sm text-slate-500">No exam results published yet.</div>
          ) : (
            <div className="space-y-2 mt-1">
              {(leaderboard.top || leaderboard.top3).slice(0, 3).map((t, i) => (
                <div key={t.student_id} className="flex items-center gap-2.5" data-testid={`leader-${i+1}`}>
                  <span className={`h-6 w-6 rounded-full flex items-center justify-center text-[11px] font-bold ${["bg-amber-100 text-amber-700","bg-slate-200 text-slate-600","bg-orange-100 text-orange-700"][i]}`}>{i+1}</span>
                  <span className="flex-1 text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{t.name}</span>
                  {t.trend === "up" && <span className="flex items-center text-emerald-600 text-xs font-medium" title={`Up from ${t.prev_percentage}%`}><TrendingUp className="h-3.5 w-3.5" /></span>}
                  {t.trend === "down" && <span className="flex items-center text-rose-500 text-xs font-medium" title={`Down from ${t.prev_percentage}%`}><TrendingDown className="h-3.5 w-3.5" /></span>}
                  {t.trend === "same" && <span className="flex items-center text-slate-400 text-xs" title="No change"><Minus className="h-3.5 w-3.5" /></span>}
                  {t.trend === "new" && <span className="text-[10px] uppercase tracking-wide text-sky-500" title="No previous exam">new</span>}
                  <span className="text-sm font-semibold text-amber-600 dark:text-amber-300 w-12 text-right">{t.percentage != null ? `${t.percentage}%` : "—"}</span>
                </div>
              ))}
            </div>
          )}
        </DashCard>

        {/* Class performance mix — students per difficulty tier */}
        <DashCard
          testid="overview-card-performance-mix"
          tone="emerald"
          icon={BarChart3}
          title="Performance mix"
          subtitle={mix ? `${mix.graded}/${mix.total} graded` : "Class 5-A"}
        >
          {loading || mix === null ? <Skel /> : mix.total === 0 ? (
            <div className="text-sm text-slate-500">No students in this class.</div>
          ) : (
            <div className="space-y-3 mt-1" data-testid="performance-mix">
              {(() => {
                const total = mix.total || 1;
                const seg = [
                  { key: "advanced", label: "Advanced", sub: ">70%", n: mix.counts.advanced || 0, bar: "bg-emerald-500", dot: "bg-emerald-500" },
                  { key: "standard", label: "Standard", sub: "40–70%", n: mix.counts.standard || 0, bar: "bg-amber-400", dot: "bg-amber-400" },
                  { key: "foundation", label: "Foundation", sub: "<40%", n: mix.counts.foundation || 0, bar: "bg-rose-500", dot: "bg-rose-500" },
                ];
                return (
                  <>
                    <div className="flex h-3 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700" data-testid="mix-bar">
                      {seg.map((s) => s.n > 0 && (
                        <div key={s.key} className={s.bar} style={{ width: `${(s.n / total) * 100}%` }} title={`${s.label}: ${s.n}`} />
                      ))}
                    </div>
                    <div className="space-y-1.5">
                      {seg.map((s) => (
                        <div key={s.key} className="flex items-center gap-2 text-sm" data-testid={`mix-row-${s.key}`}>
                          <span className={`h-2.5 w-2.5 rounded-full ${s.dot}`} />
                          <span className="flex-1 text-slate-700 dark:text-slate-200">{s.label} <span className="text-slate-400 text-xs">{s.sub}</span></span>
                          <span className="font-semibold text-slate-800 dark:text-slate-100">{s.n}</span>
                          <span className="text-xs text-slate-400 w-10 text-right">{Math.round((s.n / total) * 100)}%</span>
                        </div>
                      ))}
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </DashCard>

        <DashCard
          testid="overview-card-voice-calls"
          tone="rose"
          icon={PhoneOutgoing}
          title="Voice calls"
          subtitle="Placed today"
          onClick={() => navigate("/voice-calls")}
        >
          {loading ? <Skel /> : (
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <Pill tone="rose" icon={PhoneOutgoing} label={`${stats?.voice_calls_today?.absent ?? 0} absent`} />
                <Pill tone="pink" icon={Cake} label={`${stats?.voice_calls_today?.birthday ?? 0} birthday`} />
              </div>
              {(stats?.voice_calls_today?.failed ?? 0) > 0 && (
                <div className="text-[11px] text-rose-600 dark:text-rose-300 font-medium">
                  {stats.voice_calls_today.failed} failed — review needed
                </div>
              )}
            </div>
          )}
        </DashCard>

        {/* Attendance Analytics */}
        <DashCard
          testid="overview-card-analytics"
          tone="violet"
          icon={BarChart3}
          title="Attendance"
          subtitle="Last 30 days"
          onClick={() => navigate("/analytics")}
        >
          {loading ? <Skel /> : (
            <div className="space-y-2">
              <div className="flex items-end gap-2">
                <div className={`text-3xl font-bold ${attTone === "emerald" ? "text-emerald-600" : attTone === "sky" ? "text-sky-600 dark:text-sky-300" : "text-rose-600"}`} style={{ fontFamily: "Outfit" }}>
                  {att30}%
                </div>
                <TrendingUp className="h-4 w-4 text-slate-400 mb-1.5" />
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                {atRisk > 0 ? (
                  <span className="inline-flex items-center gap-1 text-rose-600 dark:text-rose-300">
                    <AlertTriangle className="h-3 w-3" /> {atRisk} at risk
                  </span>
                ) : (
                  <span className="text-emerald-600 dark:text-emerald-300">All students above 75%</span>
                )}
              </div>
            </div>
          )}
        </DashCard>

        {/* Marks / Academics */}
        <DashCard
          testid="overview-card-marks"
          tone="amber"
          icon={GraduationCap}
          title="Marks"
          subtitle={leaderboard?.exam_label || "Latest exam"}
          onClick={openImprovement}
        >
          {loading || leaderboard === null ? <Skel /> : leaderboard?.class_avg == null ? (
            <div className="text-sm text-slate-500">No exam results published yet.</div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-end gap-2">
                <div className="text-3xl font-bold text-amber-600 dark:text-amber-300" style={{ fontFamily: "Outfit" }} data-testid="marks-class-avg">
                  {leaderboard.class_avg}%
                </div>
                <span className="text-[11px] text-slate-400 dark:text-slate-500 mb-1.5">class avg</span>
                {leaderboard.avg_change != null && (
                  <span
                    className={`mb-1.5 inline-flex items-center gap-0.5 text-xs font-semibold ${leaderboard.avg_change > 0 ? "text-emerald-600 dark:text-emerald-300" : leaderboard.avg_change < 0 ? "text-rose-500" : "text-slate-400"}`}
                    title={`vs ${leaderboard.prev_exam_label || "previous exam"} (${leaderboard.prev_class_avg}%)`}
                    data-testid="marks-avg-change"
                  >
                    {leaderboard.avg_change > 0 ? <TrendingUp className="h-3.5 w-3.5" /> : leaderboard.avg_change < 0 ? <TrendingDown className="h-3.5 w-3.5" /> : <Minus className="h-3.5 w-3.5" />}
                    {leaderboard.avg_change > 0 ? "+" : ""}{leaderboard.avg_change}%
                  </span>
                )}
              </div>
              {leaderboard.most_improved ? (
                <div
                  data-testid="marks-most-improved"
                  className="mt-1 flex items-center gap-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 pl-2.5 pr-1.5 py-1"
                >
                  <Rocket className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-300 shrink-0" />
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); navigate(`/search?q=${encodeURIComponent(leaderboard.most_improved.roll_number || leaderboard.most_improved.name)}`); }}
                    className="flex-1 min-w-0 text-left text-[11px] text-slate-600 dark:text-slate-300 truncate hover:text-emerald-700 dark:hover:text-emerald-200"
                    title={`View ${leaderboard.most_improved.name}`}
                    data-testid="marks-most-improved-link"
                  >
                    Most improved: <span className="font-semibold text-slate-800 dark:text-slate-100">{leaderboard.most_improved.name}</span>
                    <span className="ml-1 font-semibold text-emerald-600 dark:text-emerald-300">+{leaderboard.most_improved.jump}%</span>
                  </button>
                  {canCallParents && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setCallTarget({ ...leaderboard.most_improved, mode: "congrats" }); }}
                      className="shrink-0 h-6 w-6 rounded-full flex items-center justify-center bg-emerald-500 text-white hover:bg-emerald-600 transition-colors"
                      title="Call parent to congratulate"
                      data-testid="marks-congrats-call-btn"
                    >
                      <Phone className="h-3 w-3" />
                    </button>
                  )}
                </div>
              ) : (
                <div className="text-[11px] text-slate-400 dark:text-slate-500">Most improved appears after a 2nd exam.</div>
              )}
              {leaderboard.weakest_subject && (
                <div className="flex items-center gap-1.5 text-[11px] text-slate-500 dark:text-slate-400" data-testid="marks-weakest-subject">
                  <AlertTriangle className="h-3 w-3 text-rose-400 shrink-0" />
                  Focus area: <span className="font-semibold text-slate-700 dark:text-slate-200">{leaderboard.weakest_subject.subject}</span>
                  <span className="text-rose-500 font-semibold">{leaderboard.weakest_subject.avg_pct}%</span>
                </div>
              )}
            </div>
          )}
        </DashCard>

        {/* Student Search */}
        <DashCard
          testid="overview-card-search"
          tone="slate"
          icon={Search}
          title="Student search"
          subtitle="Quick lookup"
          onClick={() => navigate("/search")}
        >
          {loading ? <Skel /> : (
            <div className="flex items-center gap-3">
              <Users className="h-5 w-5 text-slate-400" />
              <div>
                <div className="text-3xl font-bold text-slate-900 dark:text-slate-100" style={{ fontFamily: "Outfit" }}>{stats?.enrolled ?? 0}</div>
                <div className="text-[11px] uppercase tracking-[0.15em] text-slate-400 dark:text-slate-500">enrolled</div>
              </div>
            </div>
          )}
        </DashCard>
      </div>

      <TutorAuditTile
        tutorTop={tutorTop}
        classLabel={classLabel}
        sec={sec}
        onViewAll={() => navigate("/admin/audit")}
      />

      <RankingDialog
        open={rankOpen}
        onOpenChange={setRankOpen}
        marksDetail={marksDetail}
        leaderboard={leaderboard}
        detailLoading={detailLoading}
        cn={cn}
        sec={sec}
      />

      <ImprovementDialog
        open={improveOpen}
        onOpenChange={setImproveOpen}
        marksDetail={marksDetail}
        leaderboard={leaderboard}
        detailLoading={detailLoading}
        canCallParents={canCallParents}
        improvers={improvers}
        droppers={droppers}
        setBatchTarget={setBatchTarget}
        setCallTarget={setCallTarget}
      />

      <SingleCallDialog
        callTarget={callTarget}
        setCallTarget={setCallTarget}
        callSending={callSending}
        sendSingleCall={sendSingleCall}
        marksDetail={marksDetail}
        leaderboard={leaderboard}
      />

      <BatchCallDialog
        batchTarget={batchTarget}
        setBatchTarget={setBatchTarget}
        callSending={callSending}
        sendBatchCalls={sendBatchCalls}
        marksDetail={marksDetail}
      />
    </Layout>
  );
}

